import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useWhitelistRole } from '../useWhitelistRole';
import useBreakpoint from '../hooks/useBreakpoint';
import { sheet, toastMobile, tap, pageMobile } from '../mobileStyles';

const UNIDAD_OPTIONS = ['m²', 'm', 'm³', 'unidad', 'rollo', 'pieza', 'kg', 'l', 'global'];

const EMPTY_DRAFT = {
  material: '',
  proveedor: '',
  unidad: 'm²',
  precio_unitario: '',
  minimo: '',
  notas: '',
};

function formatPrice(n) {
  if (n == null || n === '') return null;
  const num = Number(n);
  if (Number.isNaN(num)) return null;
  return `S/ ${num.toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatNumber(n) {
  if (n == null || n === '') return null;
  const num = Number(n);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString('es-PE', { maximumFractionDigits: 2 });
}

export default function MetradosCostos() {
  const { isAdmin } = useWhitelistRole();
  const { isMobile, isCompact } = useBreakpoint();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // { draft, editingId? } | null
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('metrados_costos')
      .select('*')
      .order('material', { ascending: true });
    if (error) { showToast('Error cargando: ' + error.message, 'error'); setLoading(false); return; }
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.material || '').toLowerCase().includes(q) ||
      (r.proveedor || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  function openAdd() {
    setModal({ draft: { ...EMPTY_DRAFT } });
  }
  function openEdit(row) {
    setModal({
      editingId: row.id,
      draft: {
        material: row.material || '',
        proveedor: row.proveedor || '',
        unidad: row.unidad || 'm²',
        precio_unitario: row.precio_unitario ?? '',
        minimo: row.minimo ?? '',
        notas: row.notas || '',
      },
    });
  }
  function closeModal() { setModal(null); }

  async function save(e) {
    e.preventDefault();
    if (!modal || saving) return;
    const d = modal.draft;
    if (!d.material.trim()) return;
    setSaving(true);
    const payload = {
      material: d.material.trim(),
      proveedor: d.proveedor.trim() || null,
      unidad: d.unidad || 'm²',
      precio_unitario: d.precio_unitario === '' ? null : Number(d.precio_unitario),
      minimo: d.minimo === '' ? null : Number(d.minimo),
      notas: d.notas.trim() || null,
    };
    let resp;
    if (modal.editingId) {
      resp = await supabase.from('metrados_costos').update(payload).eq('id', modal.editingId).select();
    } else {
      resp = await supabase.from('metrados_costos').insert([payload]).select();
    }
    setSaving(false);
    if (resp.error) { showToast('Error: ' + resp.error.message, 'error'); return; }
    if (!resp.data || resp.data.length === 0) {
      showToast('No se guardó (posible bloqueo RLS en metrados_costos)', 'error');
      return;
    }
    await fetchRows();
    setModal(null);
    showToast(modal.editingId ? 'Registro actualizado' : 'Registro creado');
  }

  async function remove(row) {
    if (!row || deletingId) return;
    if (!window.confirm(`¿Eliminar "${row.material}"? Esta acción no se puede deshacer.`)) return;
    setDeletingId(row.id);
    const { data, error } = await supabase.from('metrados_costos').delete().eq('id', row.id).select();
    setDeletingId(null);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) {
      showToast('No se eliminó (posible bloqueo RLS en metrados_costos)', 'error');
      return;
    }
    await fetchRows();
    showToast('Registro eliminado');
  }

  return (
    <div style={isMobile ? { ...s.container, ...pageMobile } : s.container}>
      <header style={isMobile ? { ...s.header, ...s.headerMobile } : s.header}>
        <div>
          <h2 style={s.title}>Metrados y Costos</h2>
          <p style={isMobile ? { ...s.subtitle, ...s.textMobile } : s.subtitle}>Base de precios por material y proveedor</p>
        </div>
        {isAdmin && (
          <button style={isMobile ? { ...s.primaryBtn, ...tap } : s.primaryBtn} onClick={openAdd}>+ Agregar</button>
        )}
      </header>

      <section style={s.card}>
        <div style={isMobile ? { ...s.cardHeaderRow, ...s.cardHeaderRowMobile } : s.cardHeaderRow}>
          <div style={isMobile ? { ...s.searchWrap, ...s.searchWrapMobile } : s.searchWrap}>
            <span style={s.searchIcon} aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="9" r="6" />
                <line x1="13.5" y1="13.5" x2="17" y2="17" />
              </svg>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por material o proveedor..."
              style={isMobile ? { ...s.searchInput, ...s.searchInputMobile } : s.searchInput}
              aria-label="Buscar"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} style={isMobile ? { ...s.searchClearBtn, ...s.searchClearBtnMobile } : s.searchClearBtn} aria-label="Limpiar búsqueda">✕</button>
            )}
          </div>
          <span style={s.count}>
            {search.trim() ? `${visibleRows.length} de ${rows.length}` : `${rows.length} ${rows.length === 1 ? 'material' : 'materiales'}`}
          </span>
        </div>

        {loading ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>Cargando…</div>
        ) : rows.length === 0 ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>No hay registros todavía.{isAdmin ? ' Agrega el primero.' : ''}</div>
        ) : visibleRows.length === 0 ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>No se encontraron materiales para tu búsqueda.</div>
        ) : isCompact ? (
          <div style={s.mList}>
            {visibleRows.map(r => (
              <div key={r.id} style={s.mCard}>
                <div style={s.mTitle}>{r.material}</div>
                <div style={s.mRow}><span style={s.mLabel}>Proveedor</span><span style={s.mValue}>{r.proveedor || '—'}</span></div>
                <div style={s.mRow}><span style={s.mLabel}>Unidad</span><span style={s.mValue}>{r.unidad || '—'}</span></div>
                <div style={s.mRow}>
                  <span style={s.mLabel}>Precio unitario</span>
                  <span style={{ ...s.mValue, fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--wine)' }}>
                    {formatPrice(r.precio_unitario) || '—'}
                  </span>
                </div>
                <div style={s.mRow}>
                  <span style={s.mLabel}>Mínimo</span>
                  <span style={{ ...s.mValue, fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(r.minimo) ? `${formatNumber(r.minimo)} ${r.unidad || ''}`.trim() : '—'}
                  </span>
                </div>
                {r.notas && (
                  <div style={s.mNotes}><span style={s.mLabel}>Notas</span><div style={s.mNotesText}>{r.notas}</div></div>
                )}
                {isAdmin && (
                  <div style={s.mActions}>
                    <button type="button" style={s.mActionBtn} onClick={() => openEdit(r)}>Editar</button>
                    <button
                      type="button"
                      style={{ ...s.mActionBtn, color: '#B83B3B', borderColor: 'rgba(184,59,59,0.3)' }}
                      onClick={() => remove(r)}
                      disabled={deletingId === r.id}
                    >
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Material</th>
                  <th style={s.th}>Proveedor</th>
                  <th style={s.th}>Unidad</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Precio unitario</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Mínimo</th>
                  <th style={s.th}>Notas</th>
                  {isAdmin && <th style={{ ...s.th, textAlign: 'right' }}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(r => (
                  <tr key={r.id} style={s.tr}>
                    <td style={{ ...s.td, fontWeight: 600 }}>{r.material}</td>
                    <td style={{ ...s.td, color: 'var(--text-secondary)' }}>{r.proveedor || '—'}</td>
                    <td style={{ ...s.td, color: 'var(--text-secondary)' }}>{r.unidad || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--wine)' }}>
                      {formatPrice(r.precio_unitario) || '—'}
                    </td>
                    <td style={{ ...s.td, textAlign: 'right', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(r.minimo) ? `${formatNumber(r.minimo)} ${r.unidad || ''}`.trim() : '—'}
                    </td>
                    <td style={{ ...s.td, color: 'var(--text-secondary)', maxWidth: 280 }}>{r.notas || '—'}</td>
                    {isAdmin && (
                      <td style={{ ...s.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button type="button" style={s.linkBtn} onClick={() => openEdit(r)}>Editar</button>
                        <button
                          type="button"
                          style={{ ...s.linkBtn, color: '#B83B3B', marginLeft: 10 }}
                          onClick={() => remove(r)}
                          disabled={deletingId === r.id}
                        >
                          Eliminar
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {!isAdmin && (
        <p style={s.viewerNote}>
          Vista de solo lectura. Contacta al administrador para agregar o editar registros.
        </p>
      )}

      {/* ADD/EDIT MODAL */}
      {modal && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : s.overlay} onClick={closeModal}>
          <div style={isMobile ? { ...s.modal, ...sheet.panel } : s.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...s.modalHeaderMobile, ...sheet.header } : s.modalHeader}>
              <h3 style={s.modalTitle}>{modal.editingId ? 'Editar registro' : 'Nuevo registro'}</h3>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={save} style={isMobile ? { ...s.form, ...s.formMobile } : s.form}>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Material</span>
                <input
                  style={s.input}
                  required
                  autoFocus
                  placeholder="Ej. Microcemento suave"
                  value={modal.draft.material}
                  onChange={e => setModal(m => ({ ...m, draft: { ...m.draft, material: e.target.value } }))}
                />
              </label>
              <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Proveedor</span>
                  <input
                    style={s.input}
                    placeholder="Ej. Martín"
                    value={modal.draft.proveedor}
                    onChange={e => setModal(m => ({ ...m, draft: { ...m.draft, proveedor: e.target.value } }))}
                  />
                </label>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Unidad</span>
                  <select
                    style={s.input}
                    value={modal.draft.unidad}
                    onChange={e => setModal(m => ({ ...m, draft: { ...m.draft, unidad: e.target.value } }))}
                  >
                    {UNIDAD_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </label>
              </div>
              <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Precio unitario (S/)</span>
                  <input
                    style={s.input}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="75"
                    value={modal.draft.precio_unitario}
                    onChange={e => setModal(m => ({ ...m, draft: { ...m.draft, precio_unitario: e.target.value } }))}
                  />
                </label>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Mínimo</span>
                  <input
                    style={s.input}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="10"
                    value={modal.draft.minimo}
                    onChange={e => setModal(m => ({ ...m, draft: { ...m.draft, minimo: e.target.value } }))}
                  />
                </label>
              </div>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Notas</span>
                <textarea
                  style={{ ...s.input, minHeight: 80, resize: 'vertical', fontFamily: "'DM Sans',sans-serif" }}
                  placeholder="Observaciones o condiciones del proveedor"
                  value={modal.draft.notas}
                  onChange={e => setModal(m => ({ ...m, draft: { ...m.draft, notas: e.target.value } }))}
                />
              </label>
              <div style={isMobile ? { ...s.modalActions, ...s.modalActionsMobile, ...sheet.footer } : s.modalActions}>
                <button type="button" style={isMobile ? { ...s.cancelBtn, ...tap, flex: 1 } : s.cancelBtn} onClick={closeModal}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...s.primaryBtn, ...tap, flex: 1 } : s.primaryBtn} disabled={saving}>
                  {saving ? 'Guardando…' : (modal.editingId ? 'Guardar cambios' : 'Agregar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position:'fixed', bottom:28, left:'50%', transform:'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color:'#fff', padding:'10px 24px', borderRadius:10, fontSize:13, fontWeight:600,
          fontFamily:"'DM Sans',sans-serif", zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.2)',
          ...(isMobile ? toastMobile : {})
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

const s = {
  container: { padding:'32px 36px', maxWidth:1400 },
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  title: { fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:600, color:'var(--wine)', margin:0 },
  subtitle: { fontSize:13, color:'var(--text-secondary)', marginTop:4 },
  primaryBtn: { padding:'9px 18px', borderRadius:8, border:'none', background:'#6B1C2A', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },

  card: { background:'#fff', borderRadius:14, border:'1px solid var(--border)', overflow:'hidden' },
  cardHeaderRow: {
    display:'flex', justifyContent:'space-between', alignItems:'center', gap:16,
    padding:'18px 24px 12px', flexWrap:'wrap',
  },
  count: { fontSize:11, fontWeight:600, color:'var(--wine)', background:'rgba(107,28,42,0.06)', padding:'4px 12px', borderRadius:20 },

  searchWrap: { position:'relative', display:'flex', alignItems:'center', width:'100%', maxWidth:380 },
  searchIcon: { position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--text-light)', display:'flex', alignItems:'center', pointerEvents:'none' },
  searchInput: {
    width:'100%', padding:'8px 34px 8px 34px', borderRadius:8,
    border:'1px solid var(--border)', background:'#FDFBF8',
    fontSize:13, fontFamily:"'DM Sans',sans-serif", color:'var(--text-primary)',
    outline:'none', boxSizing:'border-box',
  },
  searchClearBtn: {
    position:'absolute', right:6, top:'50%', transform:'translateY(-50%)',
    width:20, height:20, borderRadius:10, border:'none',
    background:'rgba(107,28,42,0.08)', color:'#6B1C2A',
    fontSize:10, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0,
  },

  empty: { padding:'36px 24px', textAlign:'center', color:'var(--text-light)', fontSize:14 },

  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: {
    textAlign:'left', padding:'10px 24px', fontSize:11, fontWeight:600,
    color:'var(--text-light)', textTransform:'uppercase', letterSpacing:'0.08em',
    borderBottom:'1px solid var(--border)', whiteSpace:'nowrap',
  },
  tr: { borderBottom:'1px solid #f3efe9' },
  td: { padding:'12px 24px', fontSize:13, color:'var(--text-primary)', verticalAlign:'middle' },

  linkBtn: { border:'none', background:'transparent', color:'var(--wine)', fontSize:12, fontWeight:600, cursor:'pointer', padding:0, fontFamily:"'DM Sans',sans-serif" },

  viewerNote: { fontSize:12, color:'var(--text-light)', marginTop:14, textAlign:'center', fontStyle:'italic' },

  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(2px)' },
  modal: { background:'#fff', borderRadius:16, width:'100%', maxWidth:540, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' },
  modalHeader: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'22px 28px 0', gap:16 },
  modalTitle: { fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:600, color:'var(--wine)', margin:0 },
  closeBtn: { width:30, height:30, borderRadius:8, border:'1px solid var(--border)', background:'transparent', cursor:'pointer', fontSize:13, color:'var(--text-secondary)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },

  form: { padding:'18px 28px 26px', display:'flex', flexDirection:'column', gap:14 },
  fieldRow: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  fieldGroup: { display:'flex', flexDirection:'column', gap:5 },
  fieldLabel: { fontSize:11, fontWeight:600, color:'var(--text-secondary)', letterSpacing:'0.02em' },
  input: { padding:'8px 11px', borderRadius:7, border:'1px solid var(--border)', fontSize:13, fontFamily:"'DM Sans',sans-serif", color:'var(--text-primary)', background:'#FDFBF8', outline:'none', width:'100%', boxSizing:'border-box' },
  modalActions: { display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 },
  cancelBtn: { padding:'8px 18px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", color:'var(--text-secondary)' },

  // Mobile-only additions (merged when useBreakpoint().isMobile)
  modalHeaderMobile: { alignItems:'center', padding:'12px 16px 10px', borderBottom:'1px solid var(--border)' },
  formMobile: { padding:'16px 16px 0' },
  fieldRowMobile: { gridTemplateColumns:'1fr' },
  modalActionsMobile: { margin:'4px -16px 0', padding:'12px 16px', borderTop:'1px solid var(--border)' },

  // Phase 4 mobile layout
  textMobile: { fontSize:15 },
  headerMobile: { flexWrap:'wrap', gap:12 },
  cardHeaderRowMobile: { padding:'14px 16px 12px', gap:10 },
  searchWrapMobile: { maxWidth:'100%' },
  searchInputMobile: { minHeight:44, paddingRight:48 },
  searchClearBtnMobile: { right:0, width:44, height:44, borderRadius:8, background:'transparent', fontSize:14 },
  // Table → stacked cards (mobile + tablet)
  mList: { display:'flex', flexDirection:'column', gap:10, padding:'0 16px 16px' },
  mCard: { border:'1px solid var(--border)', borderRadius:12, background:'#FDFBF8', padding:'12px 14px', minWidth:0 },
  mTitle: { fontSize:16, fontWeight:600, color:'var(--text-primary)', marginBottom:6, overflowWrap:'anywhere' },
  mRow: { display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:12, padding:'5px 0', borderTop:'1px solid #f3efe9' },
  mLabel: { fontSize:12, fontWeight:600, color:'var(--text-light)', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 },
  mValue: { fontSize:15, color:'var(--text-secondary)', textAlign:'right', minWidth:0, overflowWrap:'anywhere' },
  mNotes: { padding:'6px 0 0', borderTop:'1px solid #f3efe9' },
  mNotesText: { fontSize:15, color:'var(--text-secondary)', marginTop:4, overflowWrap:'anywhere', whiteSpace:'pre-wrap' },
  mActions: { display:'flex', gap:8, marginTop:10 },
  mActionBtn: { ...tap, flex:1, borderRadius:8, border:'1px solid var(--border)', background:'#fff', color:'var(--wine)', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },
};
